import { useAppBridge } from '@shopify/app-bridge-react';
import { Redirect } from '@shopify/app-bridge/actions';

export interface ShopifyBillingPlan {
  id: string;
  name: string;
  price: number;
  currency: string;
  interval: 'EVERY_30_DAYS' | 'ANNUAL';
  trialDays?: number;
  features: string[];
  limits: {
    languages: number;
    translations_per_month: number;
  };
}

export interface SubscriptionStatus {
  status: 'active' | 'canceled' | 'past_due' | 'no_subscription';
  plan: string;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
}

export interface PlanValidation {
  allowed: boolean;
  plan: string;
  feature: string;
  currentUsage: number;
  reason?: string;
}

export const SHOPIFY_BILLING_PLANS: ShopifyBillingPlan[] = [
  {
    id: 'starter',
    name: 'Starter Plan',
    price: 29,
    currency: 'USD',
    interval: 'EVERY_30_DAYS',
    features: [
      '1 language',
      '500 translations per month',
      'Basic translation prompts',
      'Email support',
      'Standard processing time'
    ],
    limits: {
      languages: 1,
      translations_per_month: 500
    }
  },
  {
    id: 'professional',
    name: 'Professional Plan',
    price: 79,
    currency: 'USD',
    interval: 'EVERY_30_DAYS',
    features: [
      'Up to 10 languages',
      '15,000 translations per month',
      'Advanced translation prompts',
      'Priority support',
      'Faster processing time',
      'Bulk operations',
      'Translation history'
    ],
    limits: {
      languages: 10,
      translations_per_month: 15000
    }
  },
  {
    id: 'unlimited',
    name: 'Unlimited Plan',
    price: 149,
    currency: 'USD',
    interval: 'EVERY_30_DAYS',
    features: [
      'Unlimited languages',
      'Unlimited translations',
      'Advanced translation prompts',
      'Priority support',
      'Faster processing time',
      'Bulk operations',
      'Translation history',
      'Advanced analytics',
      'Custom integrations'
    ],
    limits: {
      languages: -1, // -1 means unlimited
      translations_per_month: -1
    }
  }
];

export interface ShopifyBillingService {
  requestBilling: (planId: string, returnUrl?: string, testMode?: boolean) => Promise<void>;
  getSubscriptionStatus: () => Promise<SubscriptionStatus>;
  getAvailablePlans: () => Promise<ShopifyBillingPlan[]>;
  validatePlanAccess: (feature: string, currentUsage: number) => Promise<PlanValidation>;
  cancelSubscription: () => Promise<void>;
  upgradeSubscription: (planId: string) => Promise<void>;
  createTestCharge: (planId: string) => Promise<any>;
}

export const useShopifyBilling = (): ShopifyBillingService => {
  let app: any = null;
  
  try {
    app = useAppBridge();
  } catch (error) {
    console.warn('AppBridge not available, using fallback billing methods');
  }

  // Helper function to get shop from various sources
  const getShopFromContext = (): string => {
    // Try to get shop from URL parameters
    let shop = new URLSearchParams(window.location.search).get('shop');
    
    // If shop parameter not found in URL, try to extract from current URL
    if (!shop) {
      const currentUrl = window.location.href;
      const shopMatch = currentUrl.match(/\/store\/([^\/]+)/);
      if (shopMatch) {
        shop = shopMatch[1] + '.myshopify.com';
      }
    }

    // If still no shop, try to get from referrer
    if (!shop && document.referrer) {
      const referrerMatch = document.referrer.match(/\/store\/([^\/]+)/);
      if (referrerMatch) {
        shop = referrerMatch[1] + '.myshopify.com';
      }
    }

    // If still no shop, try to get from parent window (if in iframe)
    if (!shop && window.parent && window.parent !== window) {
      try {
        const parentUrl = window.parent.location.href;
        const parentShopMatch = parentUrl.match(/\/store\/([^\/]+)/);
        if (parentShopMatch) {
          shop = parentShopMatch[1] + '.myshopify.com';
        }
      } catch (e) {
        // Cross-origin restrictions, can't access parent
      }
    }

    if (!shop) {
      // Use the shop from the current URL context
      shop = 'first-dev-block.myshopify.com'; // Fallback for testing
    }

    return shop;
  };

  const requestBilling = async (planId: string, returnUrl?: string, testMode: boolean = true): Promise<void> => {
    const plan = SHOPIFY_BILLING_PLANS.find(p => p.id === planId);
    if (!plan) {
      throw new Error(`Plan ${planId} not found`);
    }

    const shop = getShopFromContext();

    if (app) {
      try {
        const redirect = Redirect.create(app);
        
        // Use the correct Shopify billing URL structure for development stores
        // This follows the current Shopify billing API structure
        const billingUrl = `/admin/oauth/authorize?` +
          `client_id=${import.meta.env.VITE_SHOPIFY_API_KEY || 'a7cb75270aafc7d0198bb44eec53e8cd'}&` +
          `scope=write_own_subscription_contracts,read_own_subscription_contracts&` +
          `redirect_uri=${encodeURIComponent('https://app.translationflow.microdesk.space/api/billing/create-subscription')}&` +
          `state=${encodeURIComponent(JSON.stringify({ planId, returnUrl, testMode }))}`;

        redirect.dispatch(Redirect.Action.ADMIN_PATH, billingUrl);
      } catch (error) {
        console.warn('AppBridge redirect failed, using direct URL:', error);
        // Fall through to direct URL approach
      }
    }

    // Direct URL approach for development stores with test mode
    const billingUrl = `https://${shop}/admin/oauth/authorize?` +
      `client_id=${import.meta.env.VITE_SHOPIFY_API_KEY || 'a7cb75270aafc7d0198bb44eec53e8cd'}&` +
      `scope=write_own_subscription_contracts,read_own_subscription_contracts&` +
      `redirect_uri=${encodeURIComponent('https://app.translationflow.microdesk.space/api/billing/create-subscription')}&` +
      `state=${encodeURIComponent(JSON.stringify({ planId, returnUrl, testMode }))}`;
    
    // Open in new window/tab to avoid iframe restrictions
    window.open(billingUrl, '_blank');
  };

  // Create a test charge for development/testing
  const createTestCharge = async (planId: string): Promise<any> => {
    const plan = SHOPIFY_BILLING_PLANS.find(p => p.id === planId);
    if (!plan) {
      throw new Error(`Plan ${planId} not found`);
    }

    const shop = getShopFromContext();
    
    // This would typically use Shopify's GraphQL API to create a test charge
    // For now, we'll simulate the test charge creation
    console.log(`Creating test charge for plan: ${plan.name} on shop: ${shop}`);
    
    return {
      id: `test_charge_${Date.now()}`,
      planId,
      shop,
      test: true,
      status: 'pending_confirmation'
    };
  };

  const getSubscriptionStatus = async (): Promise<SubscriptionStatus> => {
    try {
      const response = await fetch('https://app.translationflow.microdesk.space/api/billing/subscription', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to get subscription status:', error);
      // Return fallback data if API fails
      return {
        status: 'no_subscription',
        plan: 'starter',
        currentPeriodEnd: null,
        cancelAtPeriodEnd: false
      };
    }
  };

  const getAvailablePlans = async (): Promise<ShopifyBillingPlan[]> => {
    try {
      const response = await fetch('https://app.translationflow.microdesk.space/api/billing/plans', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to get available plans:', error);
      // Return fallback plans if API fails
      return SHOPIFY_BILLING_PLANS;
    }
  };

  const validatePlanAccess = async (feature: string, currentUsage: number): Promise<PlanValidation> => {
    try {
      const response = await fetch('https://app.translationflow.microdesk.space/api/billing/validate-plan', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          feature,
          currentUsage
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Failed to validate plan access:', error);
      // Return fallback validation
      return {
        allowed: false,
        plan: 'starter',
        feature,
        currentUsage,
        reason: 'API error'
      };
    }
  };

  const cancelSubscription = async (): Promise<void> => {
    try {
      console.log('Canceling subscription...');
      
      const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://app.translationflow.microdesk.space/api';
      const token = localStorage.getItem('auth_token');
      
      const response = await fetch(`${API_BASE_URL}/billing/cancel-subscription`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cancel subscription');
      }
      
      const result = await response.json();
      console.log('Subscription cancelled successfully:', result);
      
      // Refresh the page to update the subscription status
      window.location.reload();
    } catch (error) {
      console.error('Failed to cancel subscription:', error);
      throw error;
    }
  };

  const upgradeSubscription = async (planId: string): Promise<void> => {
    try {
      // Check if we're in development mode and should use test charges
      const isDevelopment = window.location.hostname === 'localhost' ||
                           window.location.hostname.includes('dev') ||
                           window.location.hostname.includes('test');
      
      await requestBilling(planId, undefined, isDevelopment);
    } catch (error) {
      console.error('Failed to upgrade subscription:', error);
      throw error;
    }
  };

  return {
    requestBilling,
    getSubscriptionStatus,
    getAvailablePlans,
    validatePlanAccess,
    cancelSubscription,
    upgradeSubscription,
    createTestCharge
  };
};

// Helper function to format currency
export const formatCurrency = (amount: number, currency: string): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency
  }).format(amount);
};

// Helper function to get plan by ID
export const getPlanById = (planId: string): ShopifyBillingPlan | undefined => {
  return SHOPIFY_BILLING_PLANS.find(plan => plan.id === planId);
};

// Helper function to check if user can access feature based on plan
export const canAccessFeature = (
  userPlan: string,
  feature: 'languages' | 'translations',
  currentUsage: number
): boolean => {
  const plan = getPlanById(userPlan);
  if (!plan) return false;

  if (feature === 'languages') {
    return plan.limits.languages === -1 || currentUsage < plan.limits.languages;
  }

  if (feature === 'translations') {
    return plan.limits.translations_per_month === -1 || currentUsage < plan.limits.translations_per_month;
  }

  return false;
};

// Helper function to get remaining quota
export const getRemainingQuota = (
  userPlan: string,
  currentUsage: { languages: number; translations: number }
): { languages: number | string; translations: number | string } => {
  const plan = getPlanById(userPlan);
  if (!plan) {
    return { languages: 0, translations: 0 };
  }

  return {
    languages: plan.limits.languages === -1 ? 'Unlimited' : Math.max(0, plan.limits.languages - currentUsage.languages),
    translations: plan.limits.translations_per_month === -1 ? 'Unlimited' : Math.max(0, plan.limits.translations_per_month - currentUsage.translations)
  };
};

// Helper function to check if we're in test mode
export const isTestMode = (): boolean => {
  return window.location.hostname === 'localhost' ||
         window.location.hostname.includes('dev') ||
         window.location.hostname.includes('test');
}; 